import type {
  AnswersByKey,
  EmailStep,
  FlowVariant,
  FunnelOption,
  FunnelOptionFeedback,
  FunnelStep,
  LoadingStep,
  PaymentPlanOption,
  PaymentStep,
  QuestionStep,
  ResultStep,
  WelcomeStep,
} from "./types";
import { config } from "../config";

export const VIREO_SOCIAL_PROOF = {
  rating: "4.7",
  reviewCount: "540+",
} as const;

export const VIREO_SOCIAL_PROOF_INLINE = `${VIREO_SOCIAL_PROOF.reviewCount} private member stories`;

export const VIREO_RESULT_PROFILE_ITEMS = [
  "1 unlocked insight on what's limiting perception",
  "A ranked roadmap of what to improve first",
  "A 14-day optimization protocol matched to your goals",
] as const;

export const VIREO_EMAIL_VALUE_STACK = [
  "Your private facial analysis summary",
  "A ranked improvement plan with highest-impact fixes first",
  "A clear 14-day protocol to optimize perception",
] as const;

/**
 * Single source of truth for plan pricing.
 * Update prices here only. Original price is derived as totalPrice / (1 - savePercent/100).
 * Per-week is derived as totalPrice / weeksInPeriod.
 */
export const VIREO_PLANS: PaymentPlanOption[] = [
  {
    id: "monthly",
    label: "1 month",
    savePercent: 40,
    weeksInPeriod: 4.33,
    totalPrice: "$9.99",
  },
  {
    id: "quarterly",
    label: "3 months",
    savePercent: 50,
    weeksInPeriod: 13,
    totalPrice: "$24.99",
    isMostPopular: true,
  },
  {
    id: "annual",
    label: "1 year",
    savePercent: 60,
    weeksInPeriod: 52,
    totalPrice: "$47.00",
  },
];

export const VIREO_TRUST_BADGE_AVATARS = [
  { src: "/vireo/testimonials/girl-1.webp", alt: "Maya" },
  { src: "/vireo/testimonials/man-1.webp", alt: "Chris" },
  { src: "/vireo/testimonials/girl-2.webp", alt: "Priya" },
  { src: "/profile_tiny_jess.webp", alt: "Jess" },
] as const;

export const VIREO_WELCOME_RATING_TEXT = `<b>${VIREO_SOCIAL_PROOF.reviewCount}</b> private member stories`;
export const VIREO_WELCOME_TRUST_BADGE_CAPTION =
  "Private by default · No public sharing · Secure processing";
export const VIREO_FEATURED_IN_PUBLICATIONS = [
  "GQ",
  "POPSUGAR",
  "Men's Health",
] as const;

type FlowValue<T> = T | { a: T; b: T };

const split = <T>(a: T, b: T): FlowValue<T> => ({ a, b });

const isSplitValue = <T>(value: FlowValue<T>): value is { a: T; b: T } => {
  return (
    typeof value === "object" && value !== null && "a" in value && "b" in value
  );
};

const resolveByFlow = <T>(value: FlowValue<T>, flow: FlowVariant): T => {
  return isSplitValue(value) ? value[flow] : value;
};

type RawWelcomeStep = Omit<
  WelcomeStep,
  | "title"
  | "description"
  | "ratingText"
  | "trustBadgeCaption"
  | "trustBadgeAvatars"
  | "featuredIn"
  | "embeddedQuestion"
  | "heroImage"
  | "confirmLabel"
  | "heroSlides"
  | "featurePills"
  | "finePrint"
> & {
  title: FlowValue<string>;
  description: FlowValue<string[]>;
  ratingText: FlowValue<string>;
  trustBadgeCaption?: FlowValue<string>;
  trustBadgeAvatars?: FlowValue<WelcomeStep["trustBadgeAvatars"]>;
  featuredIn?: FlowValue<string[]>;
  embeddedQuestion?: FlowValue<WelcomeStep["embeddedQuestion"]>;
  heroImage?: FlowValue<string>;
  confirmLabel: FlowValue<string>;
  heroSlides?: FlowValue<WelcomeStep["heroSlides"]>;
  featurePills?: FlowValue<WelcomeStep["featurePills"]>;
  finePrint?: FlowValue<string>;
};

type RawQuestionStep = Omit<
  QuestionStep,
  "title" | "subtitle" | "helperText" | "options" | "mediaLabel" | "mediaImage"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  helperText?: FlowValue<string>;
  options: FlowValue<FunnelOption[]>;
  mediaLabel?: FlowValue<string>;
  mediaImage?: FlowValue<string>;
  optionFeedback?: Record<string, FunnelOptionFeedback>;
};

type RawAffirmationStep = Omit<
  Extract<FunnelStep, { kind: "affirmation" }>,
  "title" | "description" | "confirmLabel" | "mediaLabel" | "heroImage"
> & {
  title: FlowValue<string>;
  description: FlowValue<string[]>;
  confirmLabel: FlowValue<string>;
  heroImage?: FlowValue<string>;
  mediaLabel?: FlowValue<string>;
};

type RawLoadingStep = Omit<
  LoadingStep,
  "title" | "subtitle" | "confirmLabel" | "loadingFact"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  confirmLabel: FlowValue<string>;
  loadingFact: FlowValue<string>;
  heroImage?: FlowValue<string>;
};

type RawResultStep = Omit<
  ResultStep,
  | "title"
  | "subtitle"
  | "personaName"
  | "description"
  | "questMap"
  | "confirmLabel"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  personaName: FlowValue<string>;
  description: FlowValue<string[]>;
  questMap: FlowValue<ResultStep["questMap"]>;
  confirmLabel: FlowValue<string>;
};

type RawEmailStep = Omit<
  EmailStep,
  "title" | "subtitle" | "description" | "confirmLabel" | "secondaryCta"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  description: FlowValue<string[]>;
  confirmLabel: FlowValue<string>;
  secondaryCta?: FlowValue<string>;
};

type RawPaymentStep = Omit<
  PaymentStep,
  | "title"
  | "subtitle"
  | "description"
  | "benefits"
  | "plans"
  | "proofBullets"
  | "testimonials"
  | "confirmLabel"
  | "whyCardCopy"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  description: FlowValue<string[]>;
  benefits: FlowValue<string[]>;
  plans: FlowValue<PaymentStep["plans"]>;
  proofBullets: FlowValue<string[]>;
  testimonials: FlowValue<PaymentStep["testimonials"]>;
  confirmLabel: FlowValue<string>;
  whyCardCopy: FlowValue<string>;
};

type RawStep =
  | RawWelcomeStep
  | RawQuestionStep
  | RawAffirmationStep
  | RawLoadingStep
  | RawResultStep
  | RawEmailStep
  | RawPaymentStep;

const yesNoOptions: FunnelOption[] = [
  { id: "yes", label: "Yes", emoji: "✅" },
  { id: "no", label: "No", emoji: "❌" },
];

const COOKING_FOR_OPTIONS: FunnelOption[] = [
  {
    id: "just_me",
    label: "Dating",
    image: "/vireo/mascot/mascot-quick-meals.webp",
  },
  {
    id: "partner",
    label: "Social presence",
    image: "/vireo/mascot/mascot-family-meals.webp",
  },
  {
    id: "family",
    label: "Professional image",
    image: "/vireo/mascot/mascot-family-meals.webp",
  },
  {
    id: "friends_entertaining",
    label: "Photos & camera presence",
    image: "/vireo/mascot/mascot-photo-of-ingredients.webp",
  },
];

const COOKING_FOR_WELCOME_OPTIONS: FunnelOption[] = [
  {
    id: "just_me",
    label: "Dating",
    description: "See what changes create stronger first impressions",
    image: "/vireo/mascot/mascot-quick-meals.webp",
  },
  {
    id: "partner",
    label: "Social",
    description: "Refine how you're perceived in real conversations",
    image: "/vireo/mascot/mascot-family-meals.webp",
  },
  {
    id: "family",
    label: "Professional",
    description: "Optimize presence for work and leadership contexts",
    image: "/vireo/mascot/mascot-family-meals.webp",
  },
] as const;

// ─── Persona system ──────────────────────────────────────────────────────────

type PersonaKey =
  | "pantry_raider"
  | "steady_cook"
  | "explorer"
  | "planner"
  | "natural"
  | "kitchen_devotee";

type PersonaData = {
  heroImage: string;
  personaName: string;
  description: string[];
  questMap: ResultStep["questMap"];
};

type PersonaCopy = Omit<PersonaData, "questMap">;

const PERSONAS: Record<PersonaKey, PersonaCopy> = {
  pantry_raider: {
    heroImage: "/vireo/mascot/mascot-quick-meals.webp",
    personaName: "High Potential, Under-Optimized",
    description: [
      "Your base structure is stronger than you think. The main gap is execution consistency. VIREO helps you prioritize the first high-impact fixes so perception improves quickly.",
    ],
  },
  steady_cook: {
    heroImage: "/vireo/mascot/mascot-empty-search.webp",
    personaName: "Structured Features, Low Contrast",
    description: [
      "You already have a stable foundation, but contrast and definition are muting your presence. Small presentation shifts can unlock a sharper overall read.",
    ],
  },
  explorer: {
    heroImage: "/vireo/mascot/mascot-photo-of-ingredients.webp",
    personaName: "Strong Structure, Weak Presentation",
    description: [
      "Your facial structure carries well, but grooming and presentation choices are holding results back. Optimize framing and your perceived sharpness climbs fast.",
    ],
  },
  planner: {
    heroImage: "/vireo/mascot/mascot-empty-search.webp",
    personaName: "Balanced Foundation, Under-Refined",
    description: [
      "You are balanced overall, with no severe weak point. The opportunity is refinement: a focused roadmap can turn good baseline signals into standout presence.",
    ],
  },
  natural: {
    heroImage: "/vireo/mascot/mascot-import.webp",
    personaName: "Balanced but Low Contrast",
    description: [
      "Nothing is severely off, but key zones lack separation and clarity. Improving contrast in the right areas can significantly improve how you are perceived.",
    ],
  },
  kitchen_devotee: {
    heroImage: "/vireo/mascot/mascot-learning-skills.webp",
    personaName: "Precision-Focused Optimizer",
    description: [
      "You are already intentional and detail-oriented. VIREO helps you sequence advanced improvements so effort compounds instead of scattering.",
    ],
  },
};

export const getPersonaHeroImages = (): string[] => {
  return Object.values(PERSONAS).map((persona) => persona.heroImage);
};

const PERSONA_ALIASES: Record<string, PersonaKey> = {
  pantry_raider: "pantry_raider",
  pantryraider: "pantry_raider",
  steady_cook: "steady_cook",
  steadycook: "steady_cook",
  explorer: "explorer",
  planner: "planner",
  natural: "natural",
  kitchen_devotee: "kitchen_devotee",
  kitchendevotee: "kitchen_devotee",
};

const normalizePersonaAlias = (value: string): string => {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "_");
};

const resolvePersonaOverride = (value: string | null): PersonaKey | null => {
  if (!value) {
    return null;
  }

  const normalized = normalizePersonaAlias(value);
  return PERSONA_ALIASES[normalized] ?? null;
};

const str = (answers: AnswersByKey, key: string): string =>
  typeof answers[key] === "string" ? answers[key] : "";

const arr = (answers: AnswersByKey, key: string): string[] =>
  Array.isArray(answers[key]) ? (answers[key] as string[]) : [];

const computeTraitMap = (answers: AnswersByKey): ResultStep["questMap"] => {
  const goals = arr(answers, "cooking_goals");
  const why = arr(answers, "your_why");

  const mealCreativity: Record<string, number> = {
    quick_15: 1,
    one_pot: 1,
    healthy: 2,
    adventurous: 4,
  };
  const cuisine1Know: Record<string, number> = {
    yes_absolutely: 3,
    something_french: 2,
    heard_not_sure: 1,
    no_idea: 0,
  };
  const cuisine2Fridge: Record<string, number> = {
    all_the_time: 3,
    sometimes: 2,
    tried_once: 1,
    never: 0,
  };

  const creativityRaw =
    (mealCreativity[str(answers, "meal_preference")] ?? 2) +
    (goals.includes("new_cuisines") ? 2 : 0) +
    (goals.includes("technique") ? 1 : 0) +
    (cuisine1Know[str(answers, "cuisine_check_1")] ?? 1) +
    (str(answers, "rapid_fire_1") === "yes" ? 1 : 0) +
    (str(answers, "rapid_fire_2") === "yes" ? 1 : 0) +
    (str(answers, "new_ingredients") === "yes" ? 1 : 0);

  const weeklyScores: Record<string, number> = {
    rarely: 0,
    one_two: 1,
    three_four: 2,
    almost_daily: 3,
  };
  const confidenceScores: Record<string, number> = {
    beginner: 0,
    follow_recipe: 1,
    comfortable: 2,
    masterchef: 3,
  };
  const cookingForScores: Record<string, number> = {
    just_me: 0,
    partner: 1,
    family: 2,
    friends_entertaining: 2,
  };

  const commitmentRaw =
    (weeklyScores[str(answers, "weekly_cooking")] ?? 1) +
    (confidenceScores[str(answers, "cooking_confidence")] ?? 1) +
    (cookingForScores[str(answers, "cooking_for")] ?? 1);

  const knowledgeRaw =
    (confidenceScores[str(answers, "cooking_confidence")] ?? 1) +
    (cuisine1Know[str(answers, "cuisine_check_1")] ?? 1) +
    (cuisine2Fridge[str(answers, "cuisine_check_2")] ?? 1);

  const depthIntent: Record<string, number> = {
    simple: 1,
    full_picture: 2,
    real_depth: 3,
  };

  const intentionalityRaw =
    (depthIntent[str(answers, "cooking_depth")] ?? 2) +
    Math.min(goals.length, 5) +
    (why.includes("eat_healthier") ? 1 : 0) +
    (why.includes("family_enjoys") ? 1 : 0) +
    (why.includes("waste_stress") ? 1 : 0) +
    (why.includes("enjoy_kitchen") ? 1 : 0);

  const BASE = 2;
  const creativity = creativityRaw + BASE;
  const commitment = commitmentRaw + BASE;
  const knowledge = knowledgeRaw + BASE;
  const intentionality = intentionalityRaw + BASE;
  const total = creativity + commitment + knowledge + intentionality;

  return [
    {
      id: "creativity",
      label: "Facial harmony",
      percent: Math.round((creativity / total) * 100),
      icon: "◉",
    },
    {
      id: "commitment",
      label: "Presentation",
      percent: Math.round((commitment / total) * 100),
      icon: "◉",
    },
    {
      id: "knowledge",
      label: "Feature definition",
      percent: Math.round((knowledge / total) * 100),
      icon: "◉",
    },
    {
      id: "intentionality",
      label: "Optimization intent",
      percent: Math.round((intentionality / total) * 100),
      icon: "◉",
    },
  ].sort((a, b) => b.percent - a.percent);
};

const resolvePersonaKey = (answers: AnswersByKey): PersonaKey => {
  const depthMap: Record<string, "casual" | "moderate" | "deep"> = {
    simple: "casual",
    full_picture: "moderate",
    real_depth: "deep",
  };
  const depth = depthMap[str(answers, "cooking_depth")] ?? "moderate";

  const styleMap: Record<string, "free" | "structured"> = {
    quick_15: "structured",
    one_pot: "structured",
    healthy: "free",
    adventurous: "free",
  };
  const style = styleMap[str(answers, "meal_preference")] ?? "structured";

  if (depth === "casual" && style === "free") return "pantry_raider";
  if (depth === "casual" && style === "structured") return "steady_cook";
  if (depth === "moderate" && style === "free") return "explorer";
  if (depth === "moderate" && style === "structured") return "planner";
  if (depth === "deep" && style === "free") return "natural";
  return "kitchen_devotee";
};

export const getPersonaData = (answers: AnswersByKey): PersonaData => ({
  ...PERSONAS[resolvePersonaKey(answers)],
  questMap: computeTraitMap(answers),
});

export const resolvePersonaOverrideFromUrl = (
  urlLike: URL | string = window.location.href,
): PersonaKey | null => {
  const url = typeof urlLike === "string" ? new URL(urlLike) : urlLike;
  const override =
    resolvePersonaOverride(url.searchParams.get("persona")) ??
    resolvePersonaOverride(url.searchParams.get("persona_override"));
  return override;
};

export const getPersonaDataWithOverride = (
  answers: AnswersByKey,
  overridePersona: PersonaKey | null,
): PersonaData => ({
  ...PERSONAS[overridePersona ?? resolvePersonaKey(answers)],
  questMap: computeTraitMap(answers),
});

// ─────────────────────────────────────────────────────────────────────────────

const RAW_STEPS: RawStep[] = [
  {
    id: "welcome",
    order: 1,
    kind: "welcome",
    heroImage: split(
      "/vireo/Collateral/hero-flow-a-resized.avif",
      "/vireo/Collateral/image-use-what-you-have.avif",
    ),
    title: split(
      "What's holding your face back.",
      "Your face is judged instantly. See why.",
    ),
    description: split(
      ["Understand how your face is perceived and what to improve first."],
      [
        "Private by default.",
        "Get objective clarity on what to fix first.",
      ],
    ),
    ratingText: split(VIREO_WELCOME_RATING_TEXT, VIREO_WELCOME_RATING_TEXT),
    trustBadgeCaption: split(
      VIREO_WELCOME_TRUST_BADGE_CAPTION,
      VIREO_WELCOME_TRUST_BADGE_CAPTION,
    ),
    trustBadgeAvatars: split(
      [...VIREO_TRUST_BADGE_AVATARS],
      [...VIREO_TRUST_BADGE_AVATARS],
    ),
    featuredIn: split([...VIREO_FEATURED_IN_PUBLICATIONS], []),
    embeddedQuestion: split(undefined, {
      answerKey: "cooking_for",
      title: "Where does this matter most right now?",
      options: COOKING_FOR_WELCOME_OPTIONS,
    }),
    confirmLabel: split("Start analysis", "Start analysis"),
    finePrint: split(
      "Private by default · 60 seconds · No public sharing",
      "Private by default · 60 seconds · No public sharing",
    ),
  },
  {
    id: "cooking_for",
    order: 2,
    kind: "question",
    selection: "single",
    answerKey: "cooking_for",
    title: "Where does this matter most?",
    options: COOKING_FOR_OPTIONS,
    confirmLabel: "Continue →",
  },
  {
    id: "cooking_confidence",
    order: 3,
    kind: "question",
    selection: "single",
    answerKey: "cooking_confidence",
    title: "How clear are you on what to improve first?",
    options: [
      { id: "beginner", label: "No clear idea yet", emoji: "🌱" },
      { id: "follow_recipe", label: "I follow random advice", emoji: "📖" },
      { id: "comfortable", label: "I know a few things that work", emoji: "🧭" },
      { id: "masterchef", label: "I optimize consistently", emoji: "🏆" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "affirmation_1",
    order: 4,
    kind: "affirmation",
    progress: 20,
    title:
      "Whether you're just starting or already optimizing, VIREO meets you where you are.",
    description: [
      "Your roadmap will prioritize the highest-impact fixes first, based on your goals and context.",
    ],
    mediaLabel: "Graphic placeholder",
    confirmLabel: "Show me more →",
  },
  {
    id: "cooking_goals",
    order: 5,
    kind: "question",
    selection: "multi",
    answerKey: "cooking_goals",
    title: "What do you want to improve most?",
    helperText: "Pick as many as you like",
    minSelections: 1,
    options: [
      { id: "healthier", label: "Overall appearance", emoji: "🧩" },
      { id: "save_money", label: "Jawline definition", emoji: "📐" },
      { id: "new_cuisines", label: "Skin quality", emoji: "✨" },
      { id: "feed_family", label: "Eye area sharpness", emoji: "👁️" },
      { id: "lose_weight", label: "Facial balance", emoji: "⚖️" },
      { id: "technique", label: "Presentation and style", emoji: "🧠" },
      { id: "less_time", label: "Look better on camera", emoji: "📷" },
      { id: "less_waste", label: "First-impression impact", emoji: "⚡" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "biggest_blocker",
    order: 6,
    kind: "question",
    selection: "single",
    answerKey: "biggest_blocker",
    title: "What's your biggest blocker right now?",
    options: [
      { id: "too_tired", label: "Too many conflicting tips", emoji: "😮‍💨" },
      { id: "dont_know_what", label: "I don't know what to fix first", emoji: "🤷" },
      {
        id: "ingredients_waste",
        label: "I make changes but see little difference",
        emoji: "🧪",
      },
      { id: "takes_too_long", label: "Routines are hard to maintain", emoji: "⏳" },
      {
        id: "too_complicated",
        label: "Advice feels too technical",
        emoji: "🧩",
      },
    ],
    optionFeedback: {
      too_tired: {
        text: "That's normal, not a personal failure. VIREO filters noise and focuses only on what moves perception.",
        variant: "encouraging",
      },
      dont_know_what: {
        text: "Clarity is the missing piece. VIREO gives you an ordered plan so you know exactly what to tackle first.",
        variant: "positive",
      },
      ingredients_waste: {
        text: "Trying random fixes rarely compounds. VIREO helps you focus on changes that reinforce each other.",
        variant: "positive",
      },
      takes_too_long: {
        text: "Sustainability matters. We'll prioritize improvements that fit your schedule and are easy to repeat.",
        variant: "positive",
      },
      too_complicated: {
        text: "You don't need jargon. VIREO translates complexity into simple, objective actions you can follow.",
        variant: "encouraging",
      },
    },
    confirmLabel: "Continue →",
  },
  {
    id: "weekly_cooking",
    order: 7,
    kind: "question",
    selection: "single",
    answerKey: "weekly_cooking",
    title: "How often do you actively optimize your appearance?",
    options: [
      { id: "rarely", label: "Rarely", emoji: "🫥" },
      { id: "one_two", label: "1–2 times a week", emoji: "🗓️" },
      { id: "three_four", label: "3–4 times a week", emoji: "📈" },
      { id: "almost_daily", label: "Almost every day", emoji: "✨" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "cuisine_check_1",
    order: 8,
    kind: "question",
    selection: "single",
    answerKey: "cuisine_check_1",
    mediaLabel: "Perception awareness",
    mediaImage: "/vireo/mascot/mascot-learning-skills.webp",
    title: "Do you know which facial zones affect first impressions most?",
    options: [
      { id: "yes_absolutely", label: "Yes, clearly", emoji: "✅" },
      { id: "something_french", label: "I know a little", emoji: "🧠" },
      { id: "heard_not_sure", label: "I've heard mixed advice", emoji: "🤔" },
      { id: "no_idea", label: "Not really", emoji: "❓" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "cuisine_check_2",
    order: 9,
    kind: "question",
    selection: "single",
    answerKey: "cuisine_check_2",
    mediaImage: "/vireo/mascot/mascot-photo-of-ingredients.webp",
    title: "Have you ever changed one feature area and noticed a clear difference?",
    options: [
      { id: "all_the_time", label: "Yes, often", emoji: "🙌" },
      { id: "sometimes", label: "Sometimes", emoji: "👍" },
      {
        id: "tried_once",
        label: "Tried once, unclear outcome",
        emoji: "😅",
      },
      { id: "never", label: "Not yet", emoji: "🙈" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "meal_preference",
    order: 10,
    kind: "question",
    selection: "single",
    answerKey: "meal_preference",
    title: "Which improvement style fits you best?",
    options: [
      { id: "quick_15", label: "Fast daily wins", emoji: "⚡" },
      { id: "one_pot", label: "Simple repeatable routine", emoji: "🧱" },
      { id: "healthy", label: "Natural low-effort refinement", emoji: "🫧" },
      { id: "adventurous", label: "Deep optimization experiments", emoji: "🧪" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "affirmation_2",
    order: 11,
    kind: "affirmation",
    icon: "🍳",
    progress: 50,
    title: "VIREO is building your perception profile.",
    heroVideo: "/vireo/animated/Family-meal.mp4",
    heroImage: "/vireo/mascot/mascot-family-meals.webp",
    heroImageHasBorderRadius: true,
    heroImageBorderRadius: 20,
    heroImageMarginBottom: 10,
    description: [
      "Based on your answers, we're mapping structure, balance, and presentation factors that influence perception.",
      "<u>Objective</u>, private, and focused on what to improve first.",
    ],
    confirmLabel: "Keep building →",
  },
  {
    id: "cooking_depth",
    order: 12,
    kind: "question",
    selection: "single",
    answerKey: "cooking_depth",
    title: "How deep should your analysis go?",
    options: [
      {
        id: "simple",
        label: "Keep it simple",
        description: "Core insight + first actions",
        emoji: "🌱",
      },
      {
        id: "full_picture",
        label: "Give me the full picture",
        description: "Ranked factors + clear roadmap",
        emoji: "📖",
        badge: "Most popular",
      },
      {
        id: "real_depth",
        label: "I want real depth",
        description: "Context-based optimization protocol",
        emoji: "🎓",
      },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "rapid_fire_1",
    order: 13,
    kind: "question",
    selection: "single",
    answerKey: "rapid_fire_1",
    mediaLabel: "Quick wins",
    mediaImage: "/vireo/mascot/mascot-quick-meals.webp",
    title: "Would you follow a 14-day protocol if it focused on your highest-impact fix first?",
    options: yesNoOptions,
    confirmLabel: "Continue →",
  },
  {
    id: "rapid_fire_2",
    order: 14,
    kind: "question",
    selection: "single",
    answerKey: "rapid_fire_2",
    mediaLabel: "Context optimization",
    mediaImage: "/vireo/mascot/mascot-scan.webp",
    title: "Would context-specific guidance (dating/social/professional) be useful to you?",
    options: yesNoOptions,
    confirmLabel: "Continue →",
  },
  {
    id: "your_why",
    order: 15,
    kind: "question",
    selection: "multi",
    answerKey: "your_why",
    title: "Why does this matter to you now?",
    helperText: "Pick all that apply",
    minSelections: 1,
    options: [
      { id: "eat_healthier", label: "I want to look more put together", emoji: "🧭" },
      {
        id: "save_takeout",
        label: "I want better dating outcomes",
        emoji: "💸",
      },
      {
        id: "family_enjoys",
        label: "I want stronger social presence",
        emoji: "👥",
      },
      { id: "waste_stress", label: "I want a plan I can trust", emoji: "✅" },
      {
        id: "less_stress",
        label: "I want to stop second-guessing my look",
        emoji: "🧘",
      },
      {
        id: "enjoy_kitchen",
        label: "I want confidence in photos and video",
        emoji: "✨",
      },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "generation_loading",
    order: 16,
    kind: "loading",
    progress: 85,
    title: "Mapping your perception profile…",
    subtitle: "VIREO is identifying what limits your result and what to improve first.",
    heroVideo: "/vireo/animated/Cooking.mp4",
    heroImage: "/vireo/brand-moment-background.jpg",
    heroImageHasBorderRadius: true,
    heroImageBorderRadius: 20,
    heroImageMarginBottom: 10,
    loadingFact:
      "Most people don't need to change everything. Improving the right variables first creates the biggest perception shift.",
    modalQuestion: {
      answerKey: "new_ingredients",
      title: "Quick question: do you prefer subtle changes or visible transformation?",
      options: [
        { id: "yes", label: "Visible transformation" },
        { id: "not_really", label: "Subtle refinement" },
      ],
    },
    confirmLabel: "See my results →",
  },
  {
    id: "persona_result",
    order: 17,
    kind: "result",
    icon: "🎯",
    title: "Your perception profile is ready.",
    subtitle: "Here's the archetype we matched from your answers.",
    heroImageMarginBottom: 10,
    personaName: "The Explorer",
    description: [
      "You are closer than you think. A few targeted adjustments can materially improve how you are perceived.",
      "VIREO will prioritize what to improve first so progress is objective and trackable.",
    ],
    questMap: [
      { id: "creativity", label: "Facial harmony", percent: 30, icon: "◉" },
      { id: "commitment", label: "Presentation", percent: 28, icon: "◉" },
      { id: "knowledge", label: "Feature definition", percent: 22, icon: "◉" },
      {
        id: "intentionality",
        label: "Optimization intent",
        percent: 20,
        icon: "◉",
      },
    ],
    confirmLabel: "Save my perception profile →",
  },
  {
    id: "email_capture",
    order: 18,
    kind: "email",
    answerKey: "email",
    title: "Your perception profile is ready.",
    description: ["Where should we send it?"],
    emailPlaceholder: "your@email.com",
    confirmLabel: "Continue with email →",
    trustNote: "Free · Profile reserved for 48h · No spam",
    quotes: [
      {
        text: "I finally stopped guessing. The roadmap made it obvious what to fix first and what to ignore.",
        author: "Maya R., 34",
        weeksUsingApp: 6,
        image: "/vireo/testimonials/girl-1.webp",
      },
      {
        text: "I expected vague advice. Instead I got objective priorities and a plan I could actually follow.",
        author: "Chris L., 41",
        weeksUsingApp: 11,
        image: "/vireo/testimonials/man-1.webp",
      },
      {
        text: "The social context guidance was the difference. I knew exactly how to optimize for real situations.",
        author: "Sarah T., 29",
        weeksUsingApp: 8,
        image: "/vireo/testimonials/girl-2.webp",
      },
      {
        text: "I changed fewer things than I expected, but the right things. People noticed quickly.",
        author: "James K., 37",
        weeksUsingApp: 14,
      },
    ],
  },
  {
    id: "founding_member",
    order: 19,
    kind: "payment",
    icon: "🍳",
    answerKey: "selected_plan",
    title: "Choose your plan",
    subtitle: "Founding member",
    description: [
      "Most people don't need more tips. They need clarity on what to improve first. This is that system.",
      "Pick the plan that fits your pace. You'll finish checkout securely on Stripe.",
    ],
    plans: VIREO_PLANS,
    benefits: [
      "Founding pricing locked in",
      "Early access to new Vireo features",
      "Your private perception profile and progress history saved",
    ],
    proofBullets: [
      "Full facial breakdown with ranked improvement priorities",
      "Context-based optimization for dating, social, and professional settings",
      "Progress you can track across rescans",
    ],
    testimonials: [
      {
        text: "I finally stopped guessing. The roadmap made it obvious what to fix first and what to ignore.",
        author: "Maya R., 34",
        weeksUsingApp: 6,
        image: "/vireo/testimonials/girl-1.webp",
      },
      {
        text: "I expected vague advice. Instead I got objective priorities and a plan I could actually follow.",
        author: "Chris L., 41",
        weeksUsingApp: 11,
        image: "/vireo/testimonials/man-1.webp",
      },
      {
        text: "The social context guidance was the difference. I knew exactly how to optimize for real situations.",
        author: "Priya S., 38",
        weeksUsingApp: 18,
        image: "/vireo/testimonials/girl-2.webp",
      },
      {
        text: "The report was direct without being judgmental. It felt private, objective, and useful.",
        author: "Daniel K., 45",
        weeksUsingApp: 35,
        image: "/vireo/testimonials/man-1.webp",
      },
      {
        text: "I changed fewer things than I expected, but the right things. People noticed quickly.",
        author: "Elena M., 29",
        weeksUsingApp: 31,
        image: "/vireo/testimonials/girl-1.webp",
      },
    ],
    noChargeNote: "30-day money-back guarantee",
    securityNotes: ["Secure checkout via Stripe", "Cancel anytime"],
    appScreenshots: [
      "/vireo/app-showcase/screenshot-1-600.webp",
      "/vireo/app-showcase/screenshot-2-600.webp",
      "/vireo/app-showcase/screenshot-3-600.webp",
      "/vireo/app-showcase/screenshot-5-600.webp",
      "/vireo/app-showcase/screenshot-6-600.webp",
      "/vireo/app-showcase/screenshot-7-600.webp",
      "/vireo/app-showcase/screenshot-8-600.webp",
      "/vireo/app-showcase/screenshot-11-600.webp",
      "/vireo/app-showcase/screenshot-12-600.webp",
    ],
    founderMessage: {
      videoUrl:
        "https://highwayfoundation.b-cdn.net/Miriam%20Rose%20-%20Website/tnt-test-vid.mp4",
      posterUrl: "/vireo/app-showcase/vid-cover-744.webp",
      fallbackText:
        "I built VIREO because most people are overwhelmed by conflicting advice. If this does not give you real clarity, email me and we'll make it right.",
      founderName: "Kaila",
      founderTitle: "Founder, Vireo",
    },
    confirmLabel: "Continue to secure checkout →",
    whyCardCopy:
      "You don't need to change everything. You need to change the right things, in the right order. VIREO helps you optimize how you're perceived with objective clarity.",
  },
  {
    id: "checkout_complete",
    order: 20,
    kind: "affirmation",
    icon: "✅",
    title: "Confirming your checkout and preparing your email update.",
    description: [
      "If this takes longer than a moment, tap Try again to refresh your Stripe status.",
      "Thanks for supporting Vireo.",
    ],
    confirmLabel: "Done",
  },
];

const resolveStepByFlow = (step: RawStep, flow: FlowVariant): FunnelStep => {
  switch (step.kind) {
    case "welcome":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        description: resolveByFlow(step.description, flow),
        ratingText: resolveByFlow(step.ratingText, flow),
        heroImage: step.heroImage
          ? resolveByFlow(step.heroImage, flow) || undefined
          : undefined,
        trustBadgeCaption: step.trustBadgeCaption
          ? resolveByFlow(step.trustBadgeCaption, flow)
          : undefined,
        trustBadgeAvatars: step.trustBadgeAvatars
          ? resolveByFlow(step.trustBadgeAvatars, flow) || undefined
          : undefined,
        embeddedQuestion: step.embeddedQuestion
          ? resolveByFlow(step.embeddedQuestion, flow) || undefined
          : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        heroSlides: step.heroSlides
          ? resolveByFlow(step.heroSlides, flow) || undefined
          : undefined,
        featurePills: step.featurePills
          ? resolveByFlow(step.featurePills, flow) || undefined
          : undefined,
        finePrint: step.finePrint
          ? resolveByFlow(step.finePrint, flow)
          : undefined,
        featuredIn: step.featuredIn
          ? resolveByFlow(step.featuredIn, flow) || undefined
          : undefined,
      };
    case "question":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        helperText: step.helperText
          ? resolveByFlow(step.helperText, flow)
          : undefined,
        mediaLabel: step.mediaLabel
          ? resolveByFlow(step.mediaLabel, flow)
          : undefined,
        mediaImage: step.mediaImage
          ? resolveByFlow(step.mediaImage, flow) || undefined
          : undefined,
        options: resolveByFlow(step.options, flow),
        optionFeedback: step.optionFeedback,
      };
    case "affirmation":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        description: resolveByFlow(step.description, flow),
        heroImage: step.heroImage
          ? resolveByFlow(step.heroImage, flow) || undefined
          : undefined,
        mediaLabel: step.mediaLabel
          ? resolveByFlow(step.mediaLabel, flow)
          : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
      };
    case "loading":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        loadingFact: resolveByFlow(step.loadingFact, flow),
        heroImage: step.heroImage
          ? resolveByFlow(step.heroImage, flow) || undefined
          : undefined,
      };
    case "result":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        personaName: resolveByFlow(step.personaName, flow),
        description: resolveByFlow(step.description, flow),
        questMap: resolveByFlow(step.questMap, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
      };
    case "email": {
      const { secondaryCta: rawSecondaryCta, ...emailRest } = step;
      return {
        ...emailRest,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        description: resolveByFlow(step.description, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        ...(rawSecondaryCta != null
          ? { secondaryCta: resolveByFlow(rawSecondaryCta, flow) }
          : {}),
      };
    }
    case "payment":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        description: resolveByFlow(step.description, flow),
        benefits: resolveByFlow(step.benefits, flow),
        plans: resolveByFlow(step.plans, flow),
        proofBullets: resolveByFlow(step.proofBullets, flow),
        testimonials: resolveByFlow(step.testimonials, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        whyCardCopy: resolveByFlow(step.whyCardCopy, flow),
      };
  }
};

const getEnabledRawSteps = (flow?: FlowVariant): RawStep[] => {
  return RAW_STEPS.filter((step) => {
    if (flow === "b" && step.id === "cooking_for") {
      return false;
    }

    if (step.id === "email_capture") {
      return config.enableEmailCapture;
    }

    return true;
  });
};

export const getFlowSteps = (flow: FlowVariant): FunnelStep[] => {
  return getEnabledRawSteps(flow).map((step) => resolveStepByFlow(step, flow));
};

export const TOTAL_STEPS = getEnabledRawSteps("a").length;

export const FLOW_META: Record<FlowVariant, { name: string; slug: string }> = {
  a: { name: "Quick analysis", slug: "quick-meals" },
  b: { name: "Full analysis", slug: "full-kitchen" },
};

export type FunnelSection = {
  label: string;
  /** inclusive 0-based start index */
  start: number;
  /** inclusive 0-based end index */
  end: number;
};

const BASE_FUNNEL_SECTIONS: FunnelSection[] = [
  { label: "Profile", start: 0, end: 6 },
  { label: "Personality", start: 7, end: 14 },
  { label: "Personalise", start: 15, end: 19 },
];

export const getFunnelSections = (steps: FunnelStep[]): FunnelSection[] => {
  const maxIndex = steps.length - 1;
  return BASE_FUNNEL_SECTIONS.map((section, index) => ({
    label: section.label,
    start: Math.min(section.start, maxIndex),
    end:
      index === BASE_FUNNEL_SECTIONS.length - 1
        ? maxIndex
        : Math.min(section.end, maxIndex),
  })).filter((section) => section.start <= section.end);
};

export const computeSectionProgress = (
  currentIndex: number,
  sections: FunnelSection[],
): number[] => {
  return sections.map(({ start, end }) => {
    const size = end - start + 1;
    const completed = Math.min(Math.max(currentIndex - start, 0), size);
    return completed / size;
  });
};
